/**
 * The sale arithmetic and the stamp duty state, in one place for the whole downsizing module
 * (review of John and Kerry's file, 18 Oct 2026).
 *
 * ONE HELPER FOR THE MONEY FREED UP. Manual Control took the duty off its "net proceeds" and the
 * Strategy Analysis Setup did not, so the same home showed two different amounts freed up. Every
 * screen now reads these figures: the sale nets the price less the selling costs; the new home and
 * its duty come out of that; what is left is freed up, and what is short has to come from savings.
 *
 * THE STATE THE NEW HOME IS IN. With no state the duty was nil, which made a $3.2m purchase look
 * $273,000 better than it is in New South Wales. The setup's own answer wins; where it has none,
 * the state already recorded for this household is used, so the duty is charged from the start.
 */
import { calculateStampDuty, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface DzSaleFiguresInput {
    salePrice: number;
    sellingCosts: number;
    /** Zero when renting. */
    newHomePrice: number;
    /** The duty on the new home; ignored when renting or when no home is bought. */
    stampDuty: number;
    sellAndRent: boolean;
}

export interface DzSaleFigures {
    /** The sale price less the selling costs — before a new home, its duty or any rent. */
    netProceeds: number;
    /** The duty charged on the new home (nil when renting). */
    stampDuty: number;
    /** What is left once the new home and its duty are paid (all of the net proceeds when renting). */
    freedCapital: number;
    /** What the new home and its duty cost above the net proceeds, to come from savings. */
    buyUpShortfall: number;
}

const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};

export function dzSaleFigures(input: DzSaleFiguresInput): DzSaleFigures {
    const salePrice = Math.max(0, num(input.salePrice));
    const sellingCosts = Math.max(0, num(input.sellingCosts));
    const newHomePrice = input.sellAndRent ? 0 : Math.max(0, num(input.newHomePrice));
    const stampDuty = !input.sellAndRent && newHomePrice > 0 ? Math.max(0, num(input.stampDuty)) : 0;
    const netProceeds = Math.max(0, salePrice - sellingCosts);
    return {
        netProceeds,
        stampDuty,
        freedCapital: Math.max(0, netProceeds - newHomePrice - stampDuty),
        buyUpShortfall: input.sellAndRent ? 0 : Math.max(0, newHomePrice + stampDuty - netProceeds),
    };
}

/** The duty on a price in a state, nil where no state is known: a home to live in, or (`investment`) a property to rent out. */
export function dzDutyFor(state: string | null | undefined, price: number, use: 'home' | 'investment' = 'home'): number {
    return calculateStampDuty(state ?? '', price, use) ?? 0;
}

const stateOrNull = (v: unknown): string | null => {
    const s = typeof v === 'string' ? v.trim().toUpperCase() : '';
    return s && isStampDutyState(s) ? s : null;
};

/**
 * The state the new home is most likely in, from what the file already records.
 *
 * The client profile does not yet ask for the household's address, so there is no address state to
 * read (see the report for the schema key it needs). In order, it reads:
 *   1. the downsizing setup's own answer (goals.dzNewHomeState);
 *   2. an address state on the person, should the profile ever record one (client.state /
 *      client.addressState) — read defensively, so it works the day the key exists;
 *   3. the state of the village they live in today (retirementVillage.state);
 *   4. the state chosen in the village or granny flat setup (goals.rvPurchaseState /
 *      goals.gfPurchaseState) — where this household said it would buy;
 *   5. the state carried by the Couple Validation settings (__appData.coupleValidation.newHomeState).
 * Empty where none is known: no state, no duty, and every screen says so.
 */
export function dzDefaultNewHomeState(factFind: DraftFactFind | null | undefined): string {
    const ff = (factFind || {}) as Record<string, unknown>;
    const goals = (ff.goals || {}) as Record<string, unknown>;
    const client = (ff.client || {}) as Record<string, unknown>;
    const village = (ff.retirementVillage || {}) as Record<string, unknown>;
    const appData = (ff.__appData || {}) as Record<string, unknown>;
    const couple = (appData.coupleValidation || {}) as Record<string, unknown>;
    return stateOrNull(goals.dzNewHomeState)
        ?? stateOrNull(client.state)
        ?? stateOrNull(client.addressState)
        ?? stateOrNull(village.state)
        ?? stateOrNull(goals.rvPurchaseState)
        ?? stateOrNull(goals.gfPurchaseState)
        ?? stateOrNull(couple.newHomeState)
        ?? '';
}
